// Shared app language state.
//
// The app supports exactly two languages: English and Simplified Chinese. The
// selection lives in the Settings route and is persisted to localStorage, so
// every other surface (nav, portal menu) reads the same value.
//
// This module is intentionally framework-free and SSR-safe: nothing touches
// `window` at import time, so it can be imported from server components and
// compiled for plain-node tests.

export const LOCALES = ['en', 'zh'] as const;
export type Locale = (typeof LOCALES)[number];

/** A string that must be provided in every supported language. */
export type LocalizedText = Record<Locale, string>;

export const DEFAULT_LOCALE: Locale = 'en';

/** localStorage key holding the selected language. */
export const LOCALE_STORAGE_KEY = 'echo-engine-locale';

/**
 * Same-document notification for locale changes. `storage` events only fire in
 * *other* tabs, so the writer dispatches this to update the current one.
 */
export const LOCALE_CHANGE_EVENT = 'echo-engine:locale-change';

/** Native language names, shown untranslated in the language picker. */
export const languageNames: Record<Locale, string> = {
  en: 'English',
  zh: '简体中文',
};

/** BCP-47 tag applied to `<html lang>` when a locale is active. */
export const htmlLang: Record<Locale, string> = {
  en: 'en',
  zh: 'zh-CN',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** Coerce any stored/user value to a supported locale. */
export function parseLocale(value: unknown): Locale {
  return isLocale(value) ? value : DEFAULT_LOCALE;
}

/** Read the persisted locale. Returns the default during SSR or on failure. */
export function readStoredLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  try {
    return parseLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return DEFAULT_LOCALE;
  }
}

/**
 * Set `<html lang>` for the active locale.
 *
 * Called both when the language changes and on every mount, because the
 * server renders the document with the default language: without the second
 * call a reader who chose 简体中文 would get `lang="en"` back on every refresh,
 * which is what assistive technology and the portal's ingestion both read.
 */
export function applyDocumentLocale(locale: Locale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = htmlLang[locale];
}

// The selection, cached so reads are cheap and stable. Also the reason a
// rejected localStorage write (private browsing) still applies for the session.
let current: Locale | null = null;

/** The active locale. Safe to call on every render. */
export function getLocaleSnapshot(): Locale {
  if (current === null) current = readStoredLocale();
  return current;
}

/** The server always renders the default; the client adopts the stored value. */
export function getServerLocaleSnapshot(): Locale {
  return DEFAULT_LOCALE;
}

/** Persist the locale, update `<html lang>`, and notify this document. */
export function writeStoredLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  current = locale;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Private modes can reject writes; the in-memory selection still applies.
  }
  applyDocumentLocale(locale);
  window.dispatchEvent(new CustomEvent(LOCALE_CHANGE_EVENT, { detail: locale }));
}

/**
 * Subscribe to locale changes from this document ({@link LOCALE_CHANGE_EVENT})
 * and from other tabs (`storage`). Shaped for `useSyncExternalStore`.
 */
export function subscribeLocale(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const onLocaleChange = (event: Event) => {
    current = parseLocale((event as CustomEvent<unknown>).detail);
    onChange();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== LOCALE_STORAGE_KEY) return;
    current = parseLocale(event.newValue);
    onChange();
  };

  window.addEventListener(LOCALE_CHANGE_EVENT, onLocaleChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(LOCALE_CHANGE_EVENT, onLocaleChange);
    window.removeEventListener('storage', onStorage);
  };
}

/** Test seam: drops the cached selection so the next read hits storage again. */
export function resetLocaleCache(): void {
  current = null;
}
